/**
 * Name tags (docs/mentions.md, flag `mentionsUI`; owner, 2026-10-19: "When names are mentioned in
 * appreciations and accomplishments, there should be name tagging similar to how you'd see in
 * basically any other chat app like Slack, Discord, for example, where it's something like at this
 * person, except we're not going to include the at directly. It'll just like open the menu to select a
 * peon when you type at, or if you type the name, it will by default tag them, and it should have some
 * sort of colored highlighting or something that's consistent with that user."; then "The tagging
 * should also work in notes and in descriptions of tasks or even task titles. And if someone is tagged
 * at any point, it will notify them they've been tagged and what they've been tagged on.").
 *
 * A tag is derived, never stored: any whole-word, case-insensitive occurrence of a registered
 * household name in a piece of text. The longest name wins ("Jeff Smith" over "Jeff"), a name never
 * tags inside a longer word ("Hue" is not in "Huey"), and a possessive tags ("Jeff's" is Jeff). Typing
 * "@" opens a picker; picking replaces the "@" and what was typed after it with the plain name and a
 * space, so no "@" is ever kept. Every peon has one colour, their shirt's, and no two peons share one
 * (mentionColours: a peon whose shirt colour is taken gets a fallback colour instead).
 *
 * Pure on purpose: the server (the tag pushes), both clients and the Node tests import it.
 */
import { DEFAULT_SHIRT, SHIRT_SWATCH, isShirt, type Shirt } from "./shirts.ts";

/** One run of a text: a tagged name (`name` its registered spelling) or plain words (`name` null). */
export interface MentionSpan {
  text: string;
  name: string | null;
}

/** A letter or a digit in any script: a name ends where these stop. */
const WORDISH = /[\p{L}\p{N}]/u;

function isWordish(ch: string | undefined): boolean {
  return !!ch && WORDISH.test(ch);
}

/** The household's names fit for tagging, longest first, each once ignoring case. */
function taggable(names: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names) {
    const n = raw.trim().replace(/\s+/g, " ");
    if (!n || !isWordish(n[0]) || seen.has(n.toLowerCase())) continue;
    seen.add(n.toLowerCase());
    out.push(n);
  }
  return out.sort((a, b) => b.length - a.length);
}

/**
 * The text cut into runs, each tagged name its own run in the text's own spelling with `name` the
 * registered one. Whole words only, ignoring case, the longest name first; "Jeff's" tags "Jeff" and
 * leaves "'s" plain. A text with no names, or no names at all, is one plain run (none for "").
 */
export function mentionSpans(text: string, names: readonly string[]): MentionSpan[] {
  if (!text) return [];
  const list = taggable(names);
  if (list.length === 0) return [{ text, name: null }];
  const lower = text.toLowerCase();
  const out: MentionSpan[] = [];
  let plain = "";
  let i = 0;
  while (i < text.length) {
    let hit: string | null = null;
    if (!isWordish(text[i - 1])) {
      for (const n of list) {
        const end = i + n.length;
        if (lower.startsWith(n.toLowerCase(), i) && !isWordish(text[end])) {
          hit = n;
          break;
        }
      }
    }
    if (hit) {
      if (plain) out.push({ text: plain, name: null });
      plain = "";
      out.push({ text: text.slice(i, i + hit.length), name: hit });
      i += hit.length;
    } else {
      plain += text[i];
      i += 1;
    }
  }
  if (plain) out.push({ text: plain, name: null });
  return out;
}

/** The registered names a text tags, each once, in the order they first appear. */
export function mentionedNames(text: string | null | undefined, names: readonly string[]): string[] {
  const out: string[] = [];
  for (const s of mentionSpans(text ?? "", names)) {
    if (s.name && !out.some((n) => n.toLowerCase() === s.name!.toLowerCase())) out.push(s.name);
  }
  return out;
}

/**
 * Who a change newly tags: the names in any of `after` not already in any of `before`, never the
 * writer. A new thing passes `before` empty; an edit that keeps a tag tags nobody new.
 */
export function newlyMentioned(
  before: ReadonlyArray<string | null | undefined>,
  after: ReadonlyArray<string | null | undefined>,
  names: readonly string[],
  writer: string | null | undefined,
): string[] {
  const key = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  const had = new Set(before.flatMap((t) => mentionedNames(t, names)).map(key));
  const me = writer ? key(writer) : null;
  const out: string[] = [];
  for (const n of after.flatMap((t) => mentionedNames(t, names))) {
    const k = key(n);
    if (had.has(k) || k === me || out.some((o) => key(o) === k)) continue;
    out.push(n);
  }
  return out;
}

// ---------- The picker ----------

/** The "@" being typed before the caret: where it sits and what follows it. */
export interface MentionQuery {
  /** The index of the "@". */
  start: number;
  /** What was typed after it, up to the caret. */
  query: string;
}

/** The longest a query runs before the picker gives up on it. */
const QUERY_MAX = 30;

/**
 * The "@" the caret is typing after, or null: an "@" at the start or after a space or punctuation
 * (so an email address opens nothing), followed by letters, digits and single spaces up to the caret.
 */
export function mentionQuery(text: string, caret: number): MentionQuery | null {
  const before = text.slice(0, Math.max(0, Math.min(caret, text.length)));
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  if (isWordish(before[at - 1])) return null;
  const query = before.slice(at + 1);
  if (query.length > QUERY_MAX || /\s\s|\n|@/.test(query) || /^\s/.test(query)) return null;
  if (query && !/^[\p{L}\p{N}' .-]*$/u.test(query)) return null;
  return { start: at, query };
}

/**
 * The names the picker lists for `query`: every name starting with it, ignoring case, then the ones
 * holding it elsewhere, in the household's order; all of them for "". At most `max`.
 */
export function mentionMatches(names: readonly string[], query: string, max = 6): string[] {
  const q = query.replace(/^\s+/, "").toLowerCase();
  const list = taggable(names).sort((a, b) => a.localeCompare(b));
  if (!q) return list.slice(0, max);
  const starts = list.filter((n) => n.toLowerCase().startsWith(q));
  const holds = list.filter((n) => !starts.includes(n) && n.toLowerCase().includes(q));
  return [...starts, ...holds].slice(0, max);
}

/**
 * Whether the picker stays open for this query: it closes once a space is typed after a whole name
 * ("@Jeff ": typed out, it is already a tag) or after letters that match nobody, leaving the text as
 * typed.
 */
export function mentionPickerOpen(names: readonly string[], q: MentionQuery | null): boolean {
  if (!q) return false;
  if (/\s$/.test(q.query)) {
    const typed = q.query.trim().toLowerCase();
    if (taggable(names).some((n) => n.toLowerCase() === typed)) return false;
    if (mentionMatches(names, q.query).length === 0) return false;
  }
  return true;
}

/** The text with the "@" and its query replaced by the plain name and a space, and the caret after it. */
export function applyMention(text: string, q: MentionQuery, caret: number, name: string): { text: string; caret: number } {
  const end = Math.max(q.start + 1, Math.min(caret, text.length));
  const rest = text.slice(end);
  const insert = rest.startsWith(" ") ? name : `${name} `;
  const next = text.slice(0, q.start) + insert + rest;
  return { text: next, caret: q.start + insert.length + (rest.startsWith(" ") ? 1 : 0) };
}

// ---------- The colour ----------

/**
 * The fallback colours, for a peon whose shirt colour an earlier peon already has (owner, 2026-10-19:
 * "Colours should be determined by peon shirt colour, but make sure there are no duplicates, choose
 * another colour if duplicated"). Mid tones in hues the shirts do not wear (teal, pink, orange, lime,
 * sky, magenta, slate, indigo), each apart from every shirt and from each other once tinted, and each
 * readable behind the ink (tests/mentions.test.ts holds both).
 */
export const MENTION_PALETTE: readonly string[] = ["#1f8a80", "#d03c80", "#d0661a", "#7a9a1a", "#18a0c0", "#b030b0", "#6a7480", "#3c3cd8"];

/** The ink a tag's name is written in, on any surface: the scroll's dark line ink. */
export const MENTION_INK = "#2b1a0c";

/** How much white goes into a colour for the pill behind the name. */
const TINT_WHITE = 0.68;

function hexRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;
}

/** `hex` mixed with white by `w` (0 to 1). */
function whiten(hex: string, w: number): string {
  const [r, g, b] = hexRgb(hex);
  const mix = (v: number) => v + (255 - v) * w;
  return rgbHex([mix(r), mix(g), mix(b)]);
}

/** A tag's colours: `swatch` the peon's own colour (the pill's underline), `tint` the solid pale pill behind the name, `ink` the name. */
export interface MentionColour {
  swatch: string;
  tint: string;
  ink: string;
}

/**
 * A tag drawn in `swatch`: the pill is that colour mixed with white, solid, so the ink reads on it
 * over paper, cork or wood alike, light or dark.
 */
export function mentionColour(swatch: string): MentionColour {
  return { swatch, tint: whiten(swatch, TINT_WHITE), ink: MENTION_INK };
}

/** A peon's shirt colour: the chosen shirt's swatch, the default brown for one who never chose. */
export function shirtSwatch(shirt: string | null | undefined): string {
  return SHIRT_SWATCH[isShirt(shirt) ? (shirt as Shirt) : DEFAULT_SHIRT];
}

/**
 * The fallback colours in order, past the palette too: once all eight are taken, the eight again a
 * little lighter, then lighter still (a household that big is unlikely, but nobody shares a colour).
 */
function* fallbacks(): Generator<string> {
  for (let round = 0; round < 40; round++) {
    const w = round === 0 ? 0 : 0.6 * (1 - 0.75 ** round);
    for (const c of MENTION_PALETTE) yield round === 0 ? c : whiten(c, w);
  }
}

/**
 * Every household peon's tag colour at once, keyed by the name in lower case (shared/weekly.ts
 * lookKey), so no two peons share one. The peons go in order of their names in lower case (the names
 * list has no registration order to keep); each takes their shirt colour (the default brown for one
 * who never chose) unless an earlier peon has it, else the first fallback colour nobody has. The same
 * names and shirts give the same colours, on the web and in the app alike.
 */
export function mentionColours(
  names: readonly string[],
  shirts: Readonly<Record<string, string>> | null | undefined,
): Record<string, MentionColour> {
  const keys = [...new Set(names.map((n) => n.trim().toLowerCase()).filter(Boolean))].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const taken = new Set<string>();
  const out: Record<string, MentionColour> = {};
  let spare: Generator<string> | null = null;
  for (const k of keys) {
    let swatch = shirtSwatch(shirts?.[k]);
    if (taken.has(swatch)) {
      spare ??= fallbacks();
      let next = spare.next();
      while (!next.done && taken.has(next.value)) next = spare.next();
      if (!next.done) swatch = next.value;
    }
    taken.add(swatch);
    out[k] = mentionColour(swatch);
  }
  return out;
}

/** The shirt a peon chose, from GET /api/peons `shirts` (keyed by shared/weekly.ts lookKey: the name in lower case). */
export function shirtOf(shirts: Readonly<Record<string, string>> | null | undefined, name: string): string | null {
  return shirts?.[name.toLowerCase()] ?? null;
}

/** WCAG contrast between two hex colours (tests hold every tag to it). */
export function contrastRatio(a: string, b: string): number {
  const lum = (hex: string) => {
    const [r, g, b2] = hexRgb(hex).map((v) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b2;
  };
  const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

// ---------- The pushes ----------

/** Every tag push's title. */
export const MENTION_PUSH_TITLE = "You were tagged";

/** The first few words of a note, for its tag push. */
export function mentionSnippet(text: string, max = 60): string {
  const t = text.trim().replace(/\s+/g, " ");
  if (Array.from(t).length <= max) return t;
  const cut = Array.from(t).slice(0, max).join("");
  const space = cut.lastIndexOf(" ");
  return `${(space > max / 2 ? cut.slice(0, space) : cut).replace(/[\s.,;:!?]+$/, "")}…`;
}

/** A task's tag push body: who tagged you, and on what. */
export function mentionTaskBody(from: string, title: string): string {
  return `${from.trim()} tagged you on ${title.trim()}`;
}

/** A note's tag push body: who tagged you, and the note's first words. */
export function mentionNoteBody(from: string, body: string, photo = false): string {
  return `${from.trim()} tagged you in a ${photo ? "photo" : "note"}: ${mentionSnippet(body)}`;
}

/** A scroll line's tag push body: the lines are anonymous, so nobody is named. */
export function mentionScrollBody(kind: "appreciation" | "accomplishment"): string {
  return kind === "appreciation" ? "Someone thanked you on the scroll." : "Someone wrote about you on the scroll.";
}

// ---------- Where a tag push lands ----------

/** The web's hash route for a note's tag (src/router.ts): the open board with that note opened. */
export function noteTagHash(noteId: string): string {
  return `#/?note=${encodeURIComponent(noteId)}`;
}

/** The web's hash route for a scroll tag: the open board with the scroll in view (or that week's sheet once written). */
export function scrollTagHash(week: string): string {
  return `#/?scroll=${encodeURIComponent(week)}`;
}

/** What the open board was asked to show by a tag's link: a note to open, or the scroll of a week. */
export type BoardFocus = { note: string } | { scroll: string };

/** The board focus a route's query carries ("note=<id>" or "scroll=<week>"), or null. */
export function boardFocusOf(query: string | null | undefined): BoardFocus | null {
  if (!query) return null;
  const p = new URLSearchParams(query.replace(/^\?/, ""));
  const note = p.get("note");
  if (note) return { note };
  const scroll = p.get("scroll");
  if (scroll) return { scroll };
  return null;
}

/**
 * What the open board does with a tag's focus once it knows what it holds: open the note (bringing
 * the notice board into view), bring the notice board into view for the scroll in progress, bring
 * up the finished scroll's sheet, or nothing (the target is gone, or not loaded yet: `wait`).
 * `notes` null while the notes are still loading; `lead` the board's weekly lead (shared/weekly.ts
 * weeklyLead), `finishedWeek` the newest finished scroll's week, `loaded` once the weekly reads are in.
 */
export type BoardFocusAction = { do: "note"; id: string } | { do: "reveal" } | { do: "sheet" } | { do: "none" } | { do: "wait" };

export function boardFocusAction(
  focus: BoardFocus | null,
  board: { notes: ReadonlyArray<{ id: string }> | null; lead: { kind: "writing"; week: string } | { kind: "finished"; scroll: { week: string } } | null; finishedWeek: string | null; loaded: boolean },
): BoardFocusAction {
  if (!focus) return { do: "none" };
  if ("note" in focus) {
    if (board.notes === null) return { do: "wait" };
    return board.notes.some((n) => n.id === focus.note) ? { do: "note", id: focus.note } : { do: "none" };
  }
  if (board.lead?.kind === "writing" && board.lead.week === focus.scroll) return { do: "reveal" };
  if (board.finishedWeek === focus.scroll) return board.lead?.kind === "finished" && board.lead.scroll.week === focus.scroll ? { do: "reveal" } : { do: "sheet" };
  return board.loaded ? { do: "none" } : { do: "wait" };
}
